/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
import {
  RUN_STARTED,
  HAZARD_DATA_SUCCESS,
  SPEX_SERVICE_SUCCESS,
  PDF_GEN_SUCCESS,
} from '@hbrisk/sp3-risk-model-support/models/runStatuses.js';
import { MODEL_TYPE_3_ID } from '@hbrisk/sp3-risk-model-support/models/modelTypes/ids.js';
import { statusReached } from '@hbrisk/sp3-risk-model-support/models/runStatusHelpers.js';

// Below 100% the bar is a time-based estimate driven by `elapsed`, sliced across the
// statuses climbed through on the way to the card's `desiredStatus`. Each slice is a
// distinct forward range, so the bar never resets backward at a transition.
//
// Type 3 has an extra HAZARD_DATA_SUCCESS milestone, hence the per-type sequence.
const statusSequenceByModelType = {
  [MODEL_TYPE_3_ID]: [RUN_STARTED, HAZARD_DATA_SUCCESS, SPEX_SERVICE_SUCCESS, PDF_GEN_SUCCESS],
};
const defaultStatusSequence = [RUN_STARTED, SPEX_SERVICE_SUCCESS, PDF_GEN_SUCCESS];

const PROGRESS_LOWER_BOUND = 1;
const PROGRESS_UPPER_BOUND = 99;

const boundaryAt = (index, count) =>
  Math.round(
    PROGRESS_LOWER_BOUND + (index * (PROGRESS_UPPER_BOUND - PROGRESS_LOWER_BOUND)) / count
  );

// rateByStatus: ms of elapsed per 1% within a status' slice.
export const makeCalculatePercentComplete =
  rateByStatus => (desiredStatus, status, elapsed, modelType) => {
    if (statusReached(status, desiredStatus)) {
      return 100;
    }
    const sequence = statusSequenceByModelType[modelType] || defaultStatusSequence;
    const desiredIndex = sequence.indexOf(desiredStatus);
    const slices = desiredIndex === -1 ? sequence : sequence.slice(0, desiredIndex);
    const index = slices.indexOf(status);
    if (index === -1) {
      return PROGRESS_LOWER_BOUND;
    }
    const lower = boundaryAt(index, slices.length);
    const upper = boundaryAt(index + 1, slices.length);
    const rate = rateByStatus[status];
    const next = Math.round((elapsed || 0) / rate) + lower;
    return next < upper ? Math.max(next, lower) : upper - 1;
  };

export { statusSequenceByModelType };
